import { useEffect, useId, useRef, useState } from "react";
import type { PaletteLike } from "@picnic-field/renderer/appearance";
import type { Sticker } from "../../../lib/api/socialApi";
import { Button } from "../../../components/ui/Button";
import { Empty } from "../../../components/Shell";
import { useT } from "../../../i18n";
import type { TeacherWorkItem } from "../types";
import { SegGroup } from "../../../components/ui/Seg";
import { WorkThumb } from "./WorkThumb";

/**
 * FS-11-06 반응 집계(교사) — SCR v0.6 sfs-11-06 · SR-SOC-02.
 *  - 작품 카드(썸네일 · 별명 · 작품명 · 스티커 종류별 수 / '아직 없음') — **이름순 고정, 정렬·순위 컨트롤 없음**
 *    (서버 순서 = 별명 순을 그대로 쓴다. 반응 수로 줄 세우지 않는다)
 *  - 필터(전체 · N차시) — 거르기만 하고 순서는 바꾸지 않는다
 *  - 카드 열기 → 작품 상세(돌려 보기 · 스티커 이름별 수 · 원리 태그)
 * 개수는 교사 화면과 작품 주인에게만(학생 화면은 '내가 붙인 스티커'만).
 */
export function lessonFilters(works: readonly TeacherWorkItem[]): number[] {
  return [
    ...new Set(works.map((w) => w.lesson?.no).filter((n): n is number => typeof n === "number")),
  ].sort((a, b) => a - b);
}

export function stickerChips(
  counts: Record<string, number>,
  stickers: readonly Sticker[],
): { key: string; emoji: string; name: string; n: number }[] {
  // 카탈로그 순서(인기순 아님) → 카탈로그에 없는 키는 뒤에 키 그대로
  const known = stickers
    .filter((s) => (counts[s.key] ?? 0) > 0)
    .map((s) => ({ key: s.key, emoji: s.emoji, name: s.name, n: counts[s.key]! }));
  const rest = Object.keys(counts)
    .filter((k) => counts[k]! > 0 && !stickers.some((s) => s.key === k))
    .map((k) => ({ key: k, emoji: k, name: k, n: counts[k]! }));
  return [...known, ...rest];
}

export function ReactionsTab({
  works,
  stickers,
  palette,
}: {
  works: TeacherWorkItem[] | null;
  stickers: readonly Sticker[];
  palette: PaletteLike | null;
}) {
  const t = useT();
  const id = useId();
  const [lesson, setLesson] = useState<number | "all">("all");
  const [open, setOpen] = useState<string | null>(null);
  const headRef = useRef<HTMLHeadingElement>(null);
  const openWork = works?.find((w) => w.snapshotId === open) ?? null;
  useEffect(() => {
    if (openWork) headRef.current?.focus();
  }, [openWork]);

  if (works === null) return <p className="pf-muted">{t("common.loading")}</p>;
  const lessons = lessonFilters(works);
  const shown = lesson === "all" ? works : works.filter((w) => w.lesson?.no === lesson);

  return (
    <div className="pf-reactions" data-testid="reactions-tab">
      <section className="pf-pane pf-reactions__bar" aria-label={t("teacher.reactions.title")}>
        <h2 className="pf-h3">{t("teacher.reactions.title")}</h2>
        <span className="pf-muted" data-testid="fixed-order">
          {t("teacher.reactions.order")}
        </span>
        {lessons.length > 1 && (
          <div className="pf-reactions__filter">
            <SegGroup label={t("teacher.reactions.filter")} testId="lesson-filter">
              {(["all", ...lessons] as const).map((l) => (
                <label key={l} className="pf-seg__opt">
                  <input
                    type="radio"
                    name={`${id}-lesson`}
                    checked={lesson === l}
                    onChange={() => setLesson(l)}
                  />
                  <span>
                    {l === "all"
                      ? t("teacher.reactions.all")
                      : t("teacher.reactions.lesson", { no: l })}
                  </span>
                </label>
              ))}
            </SegGroup>
          </div>
        )}
      </section>

      {openWork && (
        <section
          className="pf-pane pf-pane--hero"
          aria-labelledby={`${id}-detail`}
          data-testid="reaction-detail"
        >
          <div className="pf-row">
            <h3 id={`${id}-detail`} ref={headRef} tabIndex={-1} className="pf-h3">
              {openWork.nickname} · {openWork.title}
            </h3>
            <Button
              variant="ghost"
              className="pf-btn--sm pf-row__end"
              data-testid="detail-close"
              onClick={() => setOpen(null)}
            >
              {t("common.close")}
            </Button>
          </div>
          <div className="pf-detail">
            <WorkThumb
              work={openWork}
              palette={palette}
              label={t("teacher.reactions.previewLabel", { name: openWork.nickname })}
              className=""
              interactive
            />
            <div>
              <p className="pf-note">
                {t("teacher.reactions.detailCount", { n: openWork.reactionTotal })}
              </p>
              <ul className="pf-chips">
                {stickerChips(openWork.reactionCounts, stickers).map((s) => (
                  <li key={s.key} className="pf-chip-static pf-chip-static--ok">
                    {s.emoji} {s.name} {s.n}
                  </li>
                ))}
              </ul>
              <ul className="pf-chips" aria-label={t("teacher.works.colTeacher")}>
                {openWork.tags.map((k) => (
                  <li key={k} className="pf-chip-static pf-chip-static--info">
                    {k}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      <section className="pf-pane" aria-label={t("teacher.reactions.cards")}>
        {shown.length === 0 ? (
          <Empty label={t("teacher.playground.noWorks")} />
        ) : (
          <ul className="pf-work-grid" data-testid="reaction-cards">
            {shown.map((w) => {
              const chips = stickerChips(w.reactionCounts, stickers);
              return (
                <li key={w.snapshotId} className="pf-work-tile" data-work={w.snapshotId}>
                  <button
                    type="button"
                    className="pf-work-tile__open"
                    aria-label={t("teacher.reactions.open", {
                      name: w.nickname,
                      title: w.title,
                      n: w.reactionTotal,
                    })}
                    onClick={() => setOpen(w.snapshotId)}
                  >
                    <WorkThumb
                      work={w}
                      palette={palette}
                      label={t("teacher.reactions.previewLabel", { name: w.nickname })}
                    />
                    <b>{w.nickname}</b>
                    <span className="pf-muted">{w.title}</span>
                  </button>
                  <span
                    className="pf-row pf-row--wrap pf-work-tile__chips"
                    data-testid="sticker-counts"
                  >
                    {chips.length === 0 ? (
                      <span className="pf-chip-static">{t("teacher.reactions.none")}</span>
                    ) : (
                      chips.map((s) => (
                        <span
                          key={s.key}
                          className="pf-chip-static pf-chip-static--ok"
                          data-sticker={s.key}
                          title={s.name}
                        >
                          {s.emoji} {s.n}
                        </span>
                      ))
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
        <div className="pf-banner pf-banner--info" role="note">
          <p className="pf-banner__text">{t("teacher.reactions.note")}</p>
        </div>
      </section>
    </div>
  );
}
